import { Component, Input, OnInit, ViewChild, ViewEncapsulation, OnDestroy, OnChanges, OutputRefSubscription } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgSelectComponent, NgOptionTemplateDirective, NgOptgroupTemplateDirective, NgMultiLabelTemplateDirective } from '@ng-select/ng-select';
import { Subscription } from 'rxjs';
import { DropdownV2SelectionService } from './dropdown-v2-selection.service';
import { NgClass, SlicePipe } from '@angular/common';
import { DeltaIconComponent } from '../delta-icon/delta-icon.component';

@Component({
    selector: 'emt-dropdown-v2',
    templateUrl: './dropdown-v2.component.html',
    styleUrls: ['./dropdown-v2.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        NgClass,
        DeltaIconComponent,
        NgSelectComponent,
        FormsModule,
        ReactiveFormsModule,
        NgOptionTemplateDirective,
        NgOptgroupTemplateDirective,
        NgMultiLabelTemplateDirective,
        SlicePipe,
    ],
})
export class DropDownV2Component implements OnInit, OnDestroy, OnChanges {
  @Input() label: string;
  @Input() placeholder?: string;
  @Input() optionList: any;
  @Input() control: FormControl;
  @Input() isDisabled: boolean;
  @Input() isMultiSelect: boolean;
  @Input() groupSelect: boolean;
  @Input() dropdownOnType?: boolean;
  @Input() arrowTop: string;
  @Input() searchFn: Function;
  @Input() selectableGroup: boolean;
  @Input() selectableGroupAsModel: boolean;
  @Input() groupBy: string;
  @Input() showDropdownArrow = true;

  @ViewChild('ngSelect', { static: true }) ngSelect: NgSelectComponent;
  @Input() width = 108;
  @Input() itemCount = 2;
  public isOpen: boolean;
  public selectedDropdownItems: string;
  private ngSelectOpenSubscription: OutputRefSubscription;
  private ngSelectCloseSubscription: OutputRefSubscription;
  private controlValueChangesSubscription: Subscription;
  private ngSelectTypeSubscription: OutputRefSubscription;
  public selectedItemsObject = [];
  public selectedGroupsArray = [];

  constructor(private dropdownSelectionService: DropdownV2SelectionService) {}

  ngOnInit() {
    // subscribe to open and close events to toggle arrow icon up and down
    this.ngSelectOpenSubscription = this.ngSelect.openEvent.subscribe(() => {
      if (this.isDisabled) {
        this.ngSelect.clearModel();
        this.ngSelect.close();
      } else if (this.dropdownOnType && !this.ngSelect.hasValue) {
        this.ngSelect.close();
      } else {
        this.isOpen = true;
        this.control.markAsDirty();
      }
    });

    // subscribe to typing events to toggle open/close on empty
    this.ngSelectTypeSubscription = this.ngSelect.searchEvent.subscribe(value => {
      if (this.dropdownOnType && value.term === '') {
        this.ngSelect.close();
      }
    });

    this.ngSelectCloseSubscription = this.ngSelect.closeEvent.subscribe(() => (this.isOpen = false));

    if (this.isMultiSelect && this.width === 108) {
      // If multi-select is enabled to 162px per the Delta style guide, else 108px.
      this.width = 162;
    }
    // render selected text
    if (this.isMultiSelect && this.control.value && this.control.value.length > 0) {
      this.selectedDropdownItems = this.control.value
        // show subLabel values when using a grouped dropdown
        .map(item => item)
        .join(', ');
    }
    this.controlValueChangesSubscription = this.control.valueChanges.subscribe(values => {
      if (this.isMultiSelect && values && values.length > 0 && !this.groupBy) {
        this.selectedDropdownItems = values
          // show subLabel values when using a grouped dropdown
          .map(item => item)
          .join(', ');
      } else if (!this.isMultiSelect && values) {
        this.dropdownSelectionService.setCarrierCode(values);
      } else {
        // reset when items are cleared or form control is reset
        this.selectedDropdownItems = null;
      }
      // this.ngSelect.clearModel();
    });
  }

  ngOnDestroy() {
    if (this.ngSelectOpenSubscription) {
      this.ngSelectOpenSubscription.unsubscribe();
    }

    if (this.ngSelectCloseSubscription) {
      this.ngSelectCloseSubscription.unsubscribe();
    }

    if (this.controlValueChangesSubscription) {
      this.controlValueChangesSubscription.unsubscribe();
    }

    if (this.ngSelectTypeSubscription) {
      this.ngSelectTypeSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes) {
    if (changes.isDisabled) {
      if (changes.isDisabled.currentValue) {
        this.control.disable();
      } else {
        this.control.enable();
      }
    }
  }

  onChange(items) {
    this.selectedItemsObject = items;
    this.checkGroups();
  }

  onAdd(item) {
    if (item.group && this.selectedGroupsArray.findIndex(group => item.group === group) === -1) {
      this.selectedGroupsArray.push(item.group);
    }
  }

  private compileSelected() {
    const groups = this.selectedGroupsArray.reduce((acc, item: string) => `${acc}${item.substr(0, 3)}, `, '');
    const items = this.selectedItemsObject.reduce((acc, item) => `${acc}${item.label.substr(0, 3)}, `, '');
    this.selectedDropdownItems = `${groups}${items}`.slice(0, -2);
  }

  clearSelected() {
    this.ngSelect.clearModel();
    this.selectedGroupsArray = [];
    this.ngSelect.handleClearClick();
    this.checkGroups();
  }

  private checkGroups() {
    if (this.groupBy) {
      this.selectedGroupsArray.map(group => {
        if (this.selectedItemsObject.findIndex(item => item.group === group) === -1) {
          this.selectedGroupsArray = this.selectedGroupsArray.filter(removeGroup => removeGroup !== group);
        }
      });
      this.compileSelected();
    }
  }

  clearItem(removeItem) {
    this.ngSelect.clearItem(removeItem);
    this.checkGroups();
  }
}
